/*
 * Single source of truth for the profession-specific SEO landing pages.
 *
 * These are "client services" use cases — businesses that operate out of a unit
 * in a mixed-use residential building with a buzzer, whose clients need to be
 * buzzed in. Each entry drives: the App.js route + <Helmet> tags, the
 * inject-seo.js static head injection, and the per-channel copy in the Hero,
 * Features, HowItWorks, CompaniesList, and Download section components.
 *
 * CommonJS (module.exports) so scripts/inject-seo.js (Node) and the React app
 * (webpack import) can both consume it.
 *
 * To add a profession: add one entry here. Routing, SEO, and sitemap pick it up
 * automatically; the section components render from `heroTitle`/`heroSubtext`/
 * `marquee`/`downloadTagline` plus the shared "client services" template.
 */
const PROFESSIONS = {
  law_office: {
    path: '/law-offices',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Law Office',
    metaDescription:
      'Give your law-office clients a smooth, professional arrival. Lowkey buzzes clients into your building automatically — no receptionist, no installation.',
    heroTitle: 'Buzz In Clients For Your Law Office',
    heroSubtext:
      'Give clients a smooth, professional arrival without leaving a consultation to answer the buzzer — Lowkey buzzes them into the building automatically.',
    marquee: ['Attorneys', 'Clients', 'Paralegals', 'Notaries', 'Process Servers', 'Couriers'],
    downloadTagline: 'Download Lowkey today to give your law-office clients a seamless arrival.',
  },
  tattoo_studio: {
    path: '/tattoo-studios',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Tattoo Studio',
    metaDescription:
      'Running a tattoo studio out of a unit with a buzzer? Lowkey buzzes clients in automatically so you never break focus mid-session.',
    heroTitle: 'Buzz In Clients For Your Tattoo Studio',
    heroSubtext:
      "Don't stop mid-session to run to the buzzer. Lowkey lets your clients into the building automatically while you keep tattooing.",
    marquee: ['Tattoo Clients', 'Walk-Ins', 'Piercing Clients', 'Apprentices', 'Suppliers', 'Couriers'],
    downloadTagline: 'Download Lowkey today so you never break focus mid-tattoo to answer the buzzer.',
  },
  massage: {
    path: '/massage-therapists',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Massage Practice',
    metaDescription:
      'Keep your massage practice calm and on schedule. Lowkey buzzes clients into your building automatically — no interrupting a session to answer the door.',
    heroTitle: 'Buzz In Clients For Your Massage Practice',
    heroSubtext:
      'Never interrupt a massage to run to the buzzer. Lowkey lets your next client into the building automatically, so your practice stays calm and on time.',
    marquee: ['Massage Clients', 'Spa Clients', 'Regulars', 'New Clients', 'Suppliers', 'Couriers'],
    downloadTagline: 'Download Lowkey today to keep your massage practice calm and on schedule.',
  },
  therapists: {
    path: '/therapists',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Therapy Practice',
    metaDescription:
      'Private practice in a building with a buzzer? Lowkey discreetly buzzes clients in for back-to-back sessions — no waiting-room staff needed.',
    heroTitle: 'Buzz In Clients For Your Therapy Practice',
    heroSubtext:
      "Keep your sessions private and on time. Lowkey buzzes each client into the building automatically, so you don't break a session to answer the door.",
    marquee: ['Therapy Clients', 'Patients', 'New Clients', 'Couples', 'Couriers', 'Suppliers'],
    downloadTagline: 'Download Lowkey today to discreetly buzz in clients between sessions.',
  },
  counselors: {
    path: '/counselors',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Counseling Practice',
    metaDescription:
      'Run a counseling practice from a unit with a buzzer? Lowkey buzzes clients in automatically for back-to-back appointments — discreet and hands-free.',
    heroTitle: 'Buzz In Clients For Your Counseling Practice',
    heroSubtext:
      'Welcome each client without leaving your office. Lowkey buzzes them into the building automatically, keeping appointments private and on schedule.',
    marquee: ['Counseling Clients', 'Couples', 'Families', 'New Clients', 'Couriers', 'Suppliers'],
    downloadTagline: 'Download Lowkey today to welcome counseling clients hands-free.',
  },
  accountants: {
    path: '/accountants',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Accounting Practice',
    metaDescription:
      'Tax-season clients lining up? Lowkey buzzes clients into your building automatically so you can work through back-to-back appointments.',
    heroTitle: 'Buzz In Clients For Your Accounting Practice',
    heroSubtext:
      'Keep working through tax season. Lowkey buzzes your clients into the building automatically, so back-to-back appointments never pull you to the door.',
    marquee: ['Tax Clients', 'Bookkeeping Clients', 'Business Owners', 'Document Drop-offs', 'Couriers', 'Suppliers'],
    downloadTagline: 'Download Lowkey today to buzz in clients through tax season hands-free.',
  },
  tutors: {
    path: '/tutors',
    clientNoun: 'students',
    seoTitle: 'Lowkey - Buzz In Students For Your Tutoring Practice',
    metaDescription:
      'Tutoring from a home or studio with a buzzer? Lowkey buzzes students and parents in automatically so every lesson starts on time.',
    heroTitle: 'Buzz In Students For Your Tutoring Practice',
    heroSubtext:
      'Start every lesson on time. Lowkey buzzes students and parents into the building automatically — no pausing a session to answer the buzzer.',
    marquee: ['Students', 'Parents', 'Music Students', 'Test-Prep Clients', 'Couriers', 'Suppliers'],
    downloadTagline: 'Download Lowkey today so every tutoring session starts on time.',
  },
  photographers: {
    path: '/photographers',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Photography Studio',
    metaDescription:
      "Shooting in a home or unit studio with a buzzer? Lowkey buzzes clients and models in automatically so you don't break a setup to answer the door.",
    heroTitle: 'Buzz In Clients For Your Photography Studio',
    heroSubtext:
      "Don't break down a setup to answer the buzzer. Lowkey lets clients, models, and assistants into your studio automatically.",
    marquee: ['Photo Clients', 'Models', 'Assistants', 'Stylists', 'Equipment Couriers', 'Suppliers'],
    downloadTagline: 'Download Lowkey today to buzz clients into your studio hands-free.',
  },
  estheticians: {
    path: '/estheticians',
    clientNoun: 'clients',
    seoTitle: 'Lowkey - Buzz In Clients For Your Esthetics Studio',
    metaDescription:
      'Running a facial, lash, or nail studio out of a unit with a buzzer? Lowkey buzzes clients in automatically so you never interrupt a treatment.',
    heroTitle: 'Buzz In Clients For Your Esthetics Studio',
    heroSubtext:
      'Never interrupt a treatment to answer the buzzer. Lowkey lets your clients into the building automatically, keeping your studio calm and on schedule.',
    marquee: ['Facial Clients', 'Lash Clients', 'Nail Clients', 'Waxing Clients', 'Regulars', 'Couriers'],
    downloadTagline: 'Download Lowkey today to keep your esthetics studio calm and on time.',
  },
};

module.exports = { PROFESSIONS };
